// CadeadoLead.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaLock, FaLockOpen } from "react-icons/fa";

const API_URL = (
  import.meta?.env?.VITE_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  ""
).replace(/\/+$/, "");

function resolveToken(getAuthToken) {
  try {
    if (typeof getAuthToken === "function") {
      const t = getAuthToken();
      if (t) return t;
    }
  } catch {}
  try {
    const u = localStorage.getItem("usuarioLogado");
    if (u) {
      const tok = JSON.parse(u)?.token;
      if (tok) return tok;
    }
  } catch {}
  return (
    localStorage.getItem("token") || localStorage.getItem("authToken") || ""
  );
}

export default function CadeadoLead({
  leadId,
  cadeado,
  usuarioAtual,
  empresaId,
  onStatusChange,
  onError,
  onSuccess,
  getAuthToken,
  endpointPrefix = "/bloqueio-leads",
  recarregarDepois = true,
}) {
  const [modalAberto, setModalAberto] = useState(false);
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [acao, setAcao] = useState("desbloquear"); // "bloquear" | "desbloquear"
  const [cadeadoLocal, setCadeadoLocal] = useState(Number(cadeado) ? 1 : 0);
  const [motivoAtual, setMotivoAtual] = useState("");
  const [motivoNovo, setMotivoNovo] = useState("");

  // mini-login admin (para refazer a ação com token de administrador)
  const [modoAdmin, setModoAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminSenha, setAdminSenha] = useState("");

  const cardRef = useRef(null);

  useEffect(() => {
    setCadeadoLocal(Number(cadeado) ? 1 : 0);
  }, [cadeado]);

  const headers = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    const token = resolveToken(getAuthToken);
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [getAuthToken]);

  const resolvedEmpresaId = useMemo(() => {
    const n = Number(empresaId ?? usuarioAtual?.empresa_id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [empresaId, usuarioAtual]);

  const atualizarCadeado = (novo) => {
    setCadeadoLocal(novo);
    onStatusChange?.(novo);
  };

  const emitirErro = (m) => {
    setErro(m);
    onError?.(m);
  };
  const emitirSucesso = (m) => onSuccess?.(m);

  const onCadeadoClick = () => {
    if (loading) return;
    setAcao(cadeadoLocal === 1 ? "desbloquear" : "bloquear");
    setModalAberto(true);
    setErro("");
    setSenha("");
    setMotivoNovo("");
    setModoAdmin(false);
    setAdminEmail("");
    setAdminSenha("");
  };

  async function refreshStatus() {
    if (!resolvedEmpresaId) return;
    try {
      const url = `${API_URL}${endpointPrefix}/status?lead_id=${leadId}&empresa_id=${resolvedEmpresaId}`;
      const r = await fetch(url, {
        headers: {
          ...(headers.Authorization
            ? { Authorization: headers.Authorization }
            : {}),
        },
        credentials: "include",
      });
      const j = await r.json();
      if (typeof j?.bloqueada === "boolean")
        atualizarCadeado(j.bloqueada ? 1 : 0);
      if (typeof j?.motivo === "string") setMotivoAtual(j.motivo ?? "");
    } catch {}
  }

  async function loginAdminToken() {
    const r = await fetch(`${API_URL}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: adminEmail, senha: adminSenha }),
      credentials: "include",
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j?.token) throw new Error(j?.erro || "Falha no login admin.");
    return j.token;
  }

  async function executarAcaoComToken(altToken) {
    const base = API_URL ? `${API_URL}${endpointPrefix}` : `${endpointPrefix}`;
    const url =
      acao === "bloquear"
        ? `${base}/${leadId}/bloquear`
        : `${base}/${leadId}/desbloquear`;
    const body = {
      empresa_id: resolvedEmpresaId,
      usuario_cadeado: usuarioAtual?.id,
      senha, // ignorado pelo back; mantido por compatibilidade
      ...(acao === "bloquear" && motivoNovo.trim()
        ? { motivo: motivoNovo.trim() }
        : {}),
    };
    const hdrs = { ...headers };
    if (altToken) hdrs.Authorization = `Bearer ${altToken}`;
    const resp = await fetch(url, {
      method: "PATCH",
      headers: hdrs,
      body: JSON.stringify(body),
      credentials: "include",
    });
    if (resp.status === 409) return { ok: true, idempotent: true };
    const txt = await resp.text();
    const data = txt
      ? (() => {
          try {
            return JSON.parse(txt);
          } catch {
            return {};
          }
        })()
      : {};
    if (!resp.ok) {
      const err = new Error(data?.erro || txt || `Erro HTTP ${resp.status}`);
      err.code = resp.status;
      throw err;
    }
    return data;
  }

  const executarAcao = async () => {
    if (loading) return;
    if (!resolvedEmpresaId) return emitirErro("empresa_id é obrigatório.");
    setLoading(true);
    setErro("");
    try {
      await executarAcaoComToken(); // tenta com o token atual (usuário logado)
      if (acao === "bloquear") {
        atualizarCadeado(1);
        setMotivoAtual(motivoNovo.trim() || "");
        emitirSucesso("Lead bloqueada!");
      } else {
        atualizarCadeado(0);
        setMotivoAtual("");
        emitirSucesso("Lead desbloqueada!");
      }
      if (recarregarDepois) await refreshStatus();
      setModalAberto(false);
      setSenha("");
      setMotivoNovo("");
    } catch (e) {
      if (e?.code === 403) {
        setModoAdmin(true);
        setErro("Sem permissão. Entre com um administrador para concluir.");
      } else {
        emitirErro(e?.message || "Falha ao processar.");
      }
    } finally {
      setLoading(false);
    }
  };

  const concluirComoAdmin = async () => {
    if (loading) return;
    setLoading(true);
    setErro("");
    try {
      const adminTok = await loginAdminToken();
      await executarAcaoComToken(adminTok);
      if (acao === "bloquear") {
        atualizarCadeado(1);
        setMotivoAtual(motivoNovo.trim() || "");
        emitirSucesso("Lead bloqueada!");
      } else {
        atualizarCadeado(0);
        setMotivoAtual("");
        emitirSucesso("Lead desbloqueada!");
      }
      if (recarregarDepois) await refreshStatus();
      setModalAberto(false);
      setModoAdmin(false);
      setAdminEmail("");
      setAdminSenha("");
      setSenha("");
      setMotivoNovo("");
    } catch (err) {
      setErro(err?.message || "Falha ao executar como admin.");
    } finally {
      setLoading(false);
    }
  };

  const titulo = acao === "bloquear" ? "Bloquear Lead" : "Desbloquear Lead";
  const subtitulo =
    acao === "bloquear"
      ? "Confirme para bloquear esta lead."
      : "Confirme para desbloquear esta lead.";
  const label = loading
    ? acao === "bloquear"
      ? "Bloqueando..."
      : "Desbloqueando..."
    : acao === "bloquear"
    ? "Bloquear"
    : "Desbloquear";
  const corPrimaria = acao === "bloquear" ? "#e53935" : "#1976d2";

  return (
    <div style={{ display: "inline-block" }}>
      {cadeadoLocal === 1 ? (
        <FaLock
          color="#34a853"
          size={22}
          style={{
            cursor: loading ? "not-allowed" : "pointer",
            verticalAlign: "middle",
            opacity: loading ? 0.6 : 1,
          }}
          title={loading ? "Processando..." : "Clique para desbloquear"}
          onClick={onCadeadoClick}
        />
      ) : (
        <FaLockOpen
          color="#e53935"
          size={22}
          style={{
            cursor: loading ? "not-allowed" : "pointer",
            verticalAlign: "middle",
            opacity: loading ? 0.6 : 1,
          }}
          title={loading ? "Processando..." : "Clique para bloquear"}
          onClick={onCadeadoClick}
        />
      )}

      {modalAberto &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              fontFamily: "inherit",
            }}
            onClick={(e) => {
              if (loading) return;
              if (cardRef.current && !cardRef.current.contains(e.target)) {
                setModalAberto(false);
                setSenha("");
                setErro("");
                setModoAdmin(false);
                setAdminEmail("");
                setAdminSenha("");
              }
            }}
          >
            <form
              ref={cardRef}
              onClick={(e) => e.stopPropagation()}
              onSubmit={(e) => {
                e.preventDefault();
                executarAcao();
              }}
              style={{
                background: "#fff",
                padding: "28px 24px",
                borderRadius: 12,
                minWidth: 360,
                maxWidth: "90vw",
                boxShadow: "0 6px 32px 0 rgba(60,60,60,0.18)",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 10,
                animation: "fadeIn 180ms ease-in",
              }}
            >
              {/* header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  marginBottom: 4,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    display: "grid",
                    placeItems: "center",
                    background: acao === "bloquear" ? "#fde7e7" : "#e9f6ef",
                    boxShadow: "inset 0 0 0 1.5px rgba(0,0,0,0.05)",
                    flexShrink: 0,
                  }}
                >
                  {acao === "bloquear" ? (
                    <FaLock color="#e53935" size={18} />
                  ) : (
                    <FaLockOpen color="#34a853" size={18} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      fontSize: 14,
                      lineHeight: "18px",
                      color: acao === "bloquear" ? "#c62828" : "#1b5e20",
                    }}
                  >
                    {titulo}
                  </div>
                  <div style={{ color: "#5a5a5a", fontSize: 13, marginTop: 2 }}>
                    {subtitulo}
                  </div>
                </div>
              </div>

              {/* motivo atual (ao desbloquear) */}
              {acao === "desbloquear" && motivoAtual ? (
                <div
                  style={{
                    background: "#f6f7fb",
                    border: "1px solid #e3e6f0",
                    borderRadius: 8,
                    padding: 10,
                    fontSize: 14,
                    color: "#333",
                    width: "100%",
                  }}
                >
                  <strong>Motivo do bloqueio:</strong>
                  <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
                    {motivoAtual}
                  </div>
                </div>
              ) : null}

              {/* motivo novo (ao bloquear) */}
              {acao === "bloquear" ? (
                <textarea
                  rows={3}
                  placeholder="Motivo do bloqueio (opcional)"
                  value={motivoNovo}
                  onChange={(e) => setMotivoNovo(e.target.value)}
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 6,
                    border: "1px solid #d5d9ea",
                    fontSize: 14,
                    resize: "vertical",
                  }}
                />
              ) : null}

              {/* senha opcional (não usada pelo back) */}
              <input
                type="password"
                name="lock_password"
                autoComplete="off"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 6,
                  border: "1px solid #d5d9ea",
                  fontSize: 16,
                  boxSizing: "border-box",
                }}
                placeholder="Senha (opcional)"
              />

              {erro && (
                <div
                  style={{
                    color: "#e53935",
                    background: "#fff0f0",
                    padding: "6px 10px",
                    borderRadius: 6,
                    fontSize: 14,
                    textAlign: "center",
                  }}
                >
                  {erro}
                </div>
              )}

              {/* ações principais */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="submit"
                  disabled={loading || !resolvedEmpresaId}
                  style={{
                    background: corPrimaria,
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "10px 22px",
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {label}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!loading) {
                      setModalAberto(false);
                      setSenha("");
                      setErro("");
                      setModoAdmin(false);
                      setAdminEmail("");
                      setAdminSenha("");
                    }
                  }}
                  disabled={loading}
                  style={{
                    background: "#f5f6fa",
                    color: "#3949ab",
                    border: "1.5px solid #d5d9ea",
                    borderRadius: 6,
                    padding: "10px 22px",
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  Cancelar
                </button>
              </div>

              {/* mini-login admin (aparece se 403) */}
              {modoAdmin && (
                <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                  <div style={{ fontSize: 13, color: "#444" }}>
                    Sem permissão. Faça login de administrador para concluir:
                  </div>
                  <input
                    type="email"
                    placeholder="E-mail do administrador"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    disabled={loading}
                    style={{
                      padding: 10,
                      borderRadius: 6,
                      border: "1px solid #d5d9ea",
                      fontSize: 14,
                    }}
                  />
                  <input
                    type="password"
                    placeholder="Senha do administrador"
                    value={adminSenha}
                    onChange={(e) => setAdminSenha(e.target.value)}
                    disabled={loading}
                    style={{
                      padding: 10,
                      borderRadius: 6,
                      border: "1px solid #d5d9ea",
                      fontSize: 14,
                    }}
                  />
                  <button
                    type="button"
                    onClick={concluirComoAdmin}
                    disabled={loading || !adminEmail || !adminSenha}
                    style={{
                      background: "#1b5e20",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "10px 22px",
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: loading ? "not-allowed" : "pointer",
                      opacity: loading || !adminEmail || !adminSenha ? 0.7 : 1,
                    }}
                  >
                    Concluir como ADMIN
                  </button>
                </div>
              )}
            </form>
          </div>,
          document.body
        )}
    </div>
  );
}
